import React, { useEffect, useRef } from 'react';
import store from '../../../redux/store';
import { AppState } from '../../../redux/stateTypes';
import { useSelector } from 'react-redux';
import { getAppInsights } from '../../../services/TelemetryService';

const getPBIUtils = () => import('./PBIReportUtils');

const VALID_EVENTS = new Set([
    'BI_REPORT-SELECT_TAGS',
    'BI_REPORT-ISOLATE_TAGS',
    'BI_REPORT-APPLY_COLOR',
    'BI_REPORT-CLEAR_COLOR',
    'BI_REPORT-APPLY_PUSHPIN',
    'BI_REPORT-CLEAR_PUSHPIN'
]);

const isValidEvent = (event: MessageEvent, allowedOrigin:string): boolean => {
    // 1. Origin must match exactly
    if (!allowedOrigin || event.origin !== allowedOrigin) return false;
    // 2. Data must be an object with a string type
    if (!event.data || typeof event.data !== 'object') return false;
    const type = event.data.type;
    if (typeof type !== 'string') return false;
    // 3. Type must be in the allowlist
    if (!VALID_EVENTS.has(type)) return false;
    return true;
};

const PBINewWindowWrapper = ({ children }: { children: React.ReactNode }) => {
    const viewerState = useSelector((state: AppState)=>state.viewer);
    const pbiStateRef = useRef<string | null>();
    const pbiPushpinGroupsRef = useRef<any[] | null>();
    useEffect(() => {
        if(!viewerState?.openedBIReport){
            pbiPushpinGroupsRef.current = [];
        }
        const viewer = viewerState?.viewerInstance?.UI;
        if (!viewer) return;
        const handleCameraChange = () => {
            void getPBIUtils().then(({ getPushpinExtensionInstance }) => {
                const pushpinExtension = getPushpinExtensionInstance();
                const pushpinList = document.querySelectorAll('.pushpinPoint.pushpin-from-pbi');
                if (pushpinExtension && pushpinList) {
                    for (const e of pushpinList) {
                        e.remove()
                    }
                    if (Array.isArray(pbiPushpinGroupsRef.current) &&
                        pbiPushpinGroupsRef.current.length > 0) {
                        pushpinExtension.onShowPushpin(pbiPushpinGroupsRef.current, true);
                    }
                }
            })
        };
        viewerState?.viewerInstance?.UI.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
        return () => {
            viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
        };
    }, [viewerState?.viewerInstance?.UI, viewerState?.openedBIReport]);

    useEffect(() => {
        const currentStore: AppState = store.getState();
        let allowedOrigin = '';
        try {
            if (currentStore?.config?.clientBaseUrl) {
                const url = new URL(currentStore.config.clientBaseUrl);
                allowedOrigin = url.origin;
            }
        } catch {
            // If parsing fails, keep allowedOrigin empty to reject all messages
            allowedOrigin = '';
        }
        const messageHandler = (event: MessageEvent) => {
            if (!isValidEvent(event, allowedOrigin)) return;
            void getPBIUtils().then(({ handleBINewWindowEvents }) => {
                try {
                    handleBINewWindowEvents(event.data, pbiPushpinGroupsRef);
                    pbiStateRef.current = event.data.pbiState;
                } catch (err) {
                    getAppInsights()!.trackException({
                        exception: err as Error,
                        severityLevel: 3,
                    },{
                        message: "[PBINewWindowWrapper][messageHandler] Error",
                    });
                    // Optionally track or log error; swallow to avoid breaking other listeners
                }
            });
        };

        window.addEventListener('message', messageHandler);
        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, []);

    useEffect(()=>{
        if(viewerState?.createdSnapshotId &&
            viewerState?.openedBIReport &&
            viewerState?.isPBINewWindowActive
        ){
            const isolatedTags = viewerState?.isolatedTags || null;
            const entitiesColored = viewerState?.entitiesColored || null;
            const PbiState = pbiStateRef?.current ? pbiStateRef?.current : (viewerState?.PbiState || '');
            const pbiColored = entitiesColored?.some((e)=>e.isPBI) || false;
            const reportsToPersist =
                (viewerState?.openedBIReports && viewerState.openedBIReports.length > 0)
                    ? viewerState.openedBIReports
                    : [viewerState.openedBIReport];
            void getPBIUtils().then(async ({ handleSnapshotCreation, handleBatchSnapshotCreation }) => {
                const filtered = reportsToPersist.filter(Boolean);
                if (filtered.length > 1) {
                    await handleBatchSnapshotCreation(
                        viewerState?.createdSnapshotId ?? null,
                        filtered,
                        isolatedTags,
                        pbiColored,
                        entitiesColored,
                        PbiState
                    );
                } else {
                    await Promise.all(
                        filtered.map((report) =>
                            handleSnapshotCreation(
                                viewerState?.createdSnapshotId ?? null,
                                isolatedTags,
                                pbiColored,
                                entitiesColored,
                                PbiState,
                                report?.id ?? 0
                            )
                        )
                    );
                }
            });
        }
    },[viewerState?.createdSnapshotId])

    return <>{children}</>;
};

export default PBINewWindowWrapper;
